import React, { useState, useRef, useEffect } from "react";
import api, { API } from "@/lib/api";
import { AureloMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Sparkles, Send, Bot } from "lucide-react";

const SUGGESTIONS = ["Which deals are most likely to close this month?", "Show me leads with high buying intent", "Which opportunities are at risk?", "Summarize today's sales activity", "Create a healthcare outreach campaign"];

export default function Copilot() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, loading]);

  const send = async (text) => {
    const msg = (text || input).trim(); if (!msg || loading) return;
    setInput(""); setMessages((m) => [...m, { role: "user", text: msg }]); setLoading(true);
    const token = localStorage.getItem("aurelo_token");
    const tenant = localStorage.getItem("aurelo_tenant");
    const url = `${API}/ai/copilot/stream?message=${encodeURIComponent(msg)}&token=${token}&tenant_id=${tenant}`;
    let acc = "";
    setMessages((m) => [...m, { role: "ai", text: "", actions: [], streaming: true }]);
    try {
      await new Promise((resolve) => {
        const es = new EventSource(url);
        es.onmessage = (e) => {
          try {
            const d = JSON.parse(e.data);
            if (d.done) { es.close(); resolve(); return; }
            if (d.delta) { acc += d.delta; setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "ai", text: acc, actions: [], streaming: true }; return c; }); }
          } catch {}
        };
        es.onerror = () => { es.close(); resolve(); };
      });
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "ai", text: acc || "Sorry, I couldn't respond.", actions: [], streaming: false }; return c; });
    } finally { setLoading(false); }
  };

  return (
    <div className="h-[calc(100vh-140px)] flex flex-col max-w-3xl mx-auto">
      <div className="flex items-center gap-2 mb-4"><AureloMark size={30} /><div><h1 className="text-xl font-bold text-slate-900">Aurelo AI Copilot</h1><p className="text-sm text-slate-500">Ask anything about your revenue engine.</p></div></div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto rounded-2xl bg-white border border-slate-200 p-5 space-y-4">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <div className="h-14 w-14 rounded-2xl aurelo-gradient flex items-center justify-center mb-4"><Sparkles className="h-7 w-7 text-white" /></div>
            <h3 className="font-semibold text-slate-900">How can I help you sell smarter?</h3>
            <p className="text-sm text-slate-500 mt-1 max-w-sm">I can analyze your pipeline, score leads, draft emails and take action across your CRM.</p>
            <div className="flex flex-wrap justify-center gap-2 mt-5 max-w-lg">{SUGGESTIONS.map((s) => <button key={s} onClick={() => send(s)} className="text-xs rounded-full border border-slate-200 bg-white px-3 py-1.5 hover:border-[#7C3AED]/40 hover:bg-slate-50">{s}</button>)}</div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
            <div className={m.role === "user" ? "max-w-[80%] rounded-2xl rounded-br-md aurelo-gradient text-white px-4 py-2.5 text-sm" : "max-w-[85%] rounded-2xl rounded-bl-md bg-slate-50 border border-slate-200 px-4 py-3"}>
              {m.role === "ai" && <div className="flex items-center gap-1.5 mb-1 text-[11px] font-semibold text-[#7C3AED]"><Bot className="h-3.5 w-3.5" />Aurelo AI</div>}
              <div className={m.role === "user" ? "whitespace-pre-wrap" : "text-sm text-slate-700 whitespace-pre-wrap"}>{m.text}</div>
              {m.actions?.length > 0 && <div className="flex flex-wrap gap-1.5 mt-3">{m.actions.map((a, j) => <span key={j} className="text-[11px] font-medium rounded-lg border border-[#7C3AED]/30 bg-white text-[#7C3AED] px-2.5 py-1">{a.label}</span>)}</div>}
            </div>
          </div>
        ))}
        {loading && (messages.length === 0 || messages[messages.length-1].text === "") && <div className="flex items-center gap-2 text-sm text-slate-400"><div className="flex gap-1"><span className="h-2 w-2 rounded-full bg-slate-300 animate-bounce"/><span className="h-2 w-2 rounded-full bg-slate-300 animate-bounce" style={{animationDelay:'150ms'}}/><span className="h-2 w-2 rounded-full bg-slate-300 animate-bounce" style={{animationDelay:'300ms'}}/></div>Thinking…</div>}
      </div>
      <div className="mt-3 flex items-end gap-2 rounded-2xl border border-slate-200 bg-white p-2 focus-within:ring-2 focus-within:ring-blue-500/30">
        <textarea data-testid="copilot-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} rows={1} placeholder="Ask Aurelo AI…" className="flex-1 resize-none bg-transparent text-sm outline-none max-h-32 px-2 py-1.5" />
        <Button data-testid="copilot-send" size="icon" onClick={() => send()} disabled={loading || !input.trim()} className="aurelo-gradient text-white shrink-0"><Send className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}
